import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CalendarioAutoAlertsPanel from "@/pages/despacho/CalendarioAutoAlertsPanel";
import CalendarioConfigPanel from "@/pages/despacho/CalendarioConfigPanel";
import CalendarioClientTemplatesPanel from "@/pages/despacho/CalendarioClientTemplatesPanel";
import CalendarioClientWhatsappPanel from "@/pages/despacho/CalendarioClientWhatsappPanel";
import CalendarioEmailParamsPanel from "@/pages/despacho/CalendarioEmailParamsPanel";
import CalendarioEnvioLibrePanel from "@/pages/despacho/CalendarioEnvioLibrePanel";
import CalendarioMensajesGeneralesPanel from "@/pages/despacho/CalendarioMensajesGeneralesPanel";
import CalendarioMensajesPlantillasPanel from "@/pages/despacho/CalendarioMensajesPlantillasPanel";
import CalendarioDueRulesPanel from "@/pages/despacho/CalendarioDueRulesPanel";
import CalendarioOperationalRemindersPanel from "@/pages/despacho/CalendarioOperationalRemindersPanel";
import CalendarioSatValidationPanel from "@/pages/despacho/CalendarioSatValidationPanel";
import CalendarioReminderBundleGroupsPanel from "@/pages/despacho/CalendarioReminderBundleGroupsPanel";
import ScheduledAuxRemindersPanel from "@/pages/despacho/ScheduledAuxRemindersPanel";
import FollowupSentPanel from "@/pages/despacho/FollowupSentPanel";
import ClientReminderTasksPanel from "@/pages/despacho/ClientReminderTasksPanel";
import {
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import { DespachoInfoCallout, DespachoPageShell } from "@/components/despacho/DespachoUi";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

const SECTION_FISCAL = "fiscal";
const SECTION_OPERATIVO = "operativo";
const SECTION_CLIENTES = "clientes";
const SECTION_CLIENTES_WA = "clientes-whatsapp";
const SECTION_AUTO = "auto-alertas";
const SECTION_NONE = "none";

const SECTION_FROM_QUERY = {
  fiscal: SECTION_FISCAL,
  operativo: SECTION_OPERATIVO,
  clientes: SECTION_CLIENTES,
  "clientes-correo": SECTION_CLIENTES,
  correo: SECTION_CLIENTES,
  "clientes-whatsapp": SECTION_CLIENTES_WA,
  whatsapp: SECTION_CLIENTES_WA,
  "auto-alertas": SECTION_AUTO,
  auto: SECTION_AUTO,
  alertas: SECTION_AUTO,
  none: SECTION_NONE,
  closed: SECTION_NONE,
};

/** Pestañas principales del correo. Las URLs legacy (tipos, bandeja, etc.) siguen resolviendo. */
const CLIENTES_SUB_FROM_QUERY = {
  generales: "generales",
  "mensajes-generales": "generales",
  plantillas: "plantillas",
  parametros: "parametros",
  "parametros-correo": "parametros",
  "envio-libre": "envio-libre",
  "envio_libre": "envio-libre",
  seguimiento: "seguimiento",
  "avisos-fiscales": "seguimiento",
  // Legacy → herramientas ocultas
  "legacy-plantillas": "plantillas",
  tipos: "legacy-tipos",
  bandeja: "legacy-bandeja",
  ejecucion: "legacy-bandeja",
  whatsapp: "generales",
  correo: "parametros",
};

const CLIENTES_PRIMARY_SUBS = new Set(["generales", "plantillas", "parametros", "envio-libre", "seguimiento"]);

const AUTO_SUB_FROM_QUERY = {
  interruptores: "interruptores",
  tablero: "tablero",
  programados: "tablero",
};

const SECTIONS = [
  {
    id: SECTION_FISCAL,
    title: "Calendario fiscal",
    description: "Reglas de vencimiento, días hábiles, validación SAT y grupos de obligaciones.",
    testId: "calendario-section-fiscal",
  },
  {
    id: SECTION_OPERATIVO,
    title: "Recordatorios internos",
    description: "Plantillas de avisos operativos al auxiliar (papelería, revisiones, etc.).",
    testId: "calendario-section-operativo",
  },
  {
    id: SECTION_CLIENTES,
    title: "Recordatorios a Clientes (correo electrónico)",
    description: "Mensajes generales, plantillas, parámetros SMTP, envío libre y Avisos Enviados.",
    testId: "calendario-section-clientes",
  },
  {
    id: SECTION_CLIENTES_WA,
    title: "Recordatorios a Clientes (WhatsApp)",
    description: "Canal WhatsApp Business (próximamente). Mientras tanto use correo electrónico.",
    testId: "calendario-section-clientes-whatsapp",
  },
  {
    id: SECTION_AUTO,
    title: "Recordatorios y alertas automáticas",
    description: "Interruptores del generador y tablero de recordatorios programados al auxiliar.",
    testId: "calendario-section-auto",
  },
];

function AccordionSection({ title, description, isOpen, onToggle, children, testId }) {
  return (
    <section
      className={`rounded-sm border bg-white ${isOpen ? "border-[#0F2D5C]/40 shadow-sm" : "border-slate-200"}`}
      data-testid={testId}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={isOpen}
        onClick={onToggle}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="min-w-0">
          <span className="block text-sm font-bold text-[#0F2D5C]">{title}</span>
          {description ? (
            <span className="block text-xs text-slate-500 mt-1">{description}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {isOpen ? (
        <div className="border-t border-slate-100 p-4 space-y-4">{children}</div>
      ) : null}
    </section>
  );
}

function CalendarioFiscalBody() {
  return (
    <Tabs defaultValue="reglas" className="space-y-4">
      <TabsList className="rounded-sm flex flex-wrap h-auto gap-1">
        <TabsTrigger value="reglas" className="rounded-sm text-xs" data-testid="calendario-tab-due-rules">
          Reglas de vencimiento
        </TabsTrigger>
        <TabsTrigger value="config" className="rounded-sm text-xs">
          Configuración
        </TabsTrigger>
        <TabsTrigger value="validacion" className="rounded-sm text-xs" data-testid="calendario-tab-sat-validation">
          Validación SAT
        </TabsTrigger>
        <TabsTrigger value="familias" className="rounded-sm text-xs" data-testid="calendario-tab-reminder-families">
          Grupos de obligaciones
        </TabsTrigger>
      </TabsList>
      <TabsContent value="reglas" className="mt-0">
        <CalendarioDueRulesPanel
          scope="fiscal"
          showSampleDates
          allowCustomAdd
          testId="calendario-fiscal-due-rules"
          noticeText="Reglas de vencimiento legal por obligación (SAT, IMSS, INFONAVIT, REPSE, estatales). La generación de instancias está en Tareas → Generar. El tablero de avisos al auxiliar está en Recordatorios y alertas automáticas (sección plegable abajo)."
        />
      </TabsContent>
      <TabsContent value="config" className="mt-0">
        <CalendarioConfigPanel />
      </TabsContent>
      <TabsContent value="validacion" className="mt-0">
        <CalendarioSatValidationPanel />
      </TabsContent>
      <TabsContent value="familias" className="mt-0">
        <CalendarioReminderBundleGroupsPanel />
      </TabsContent>
    </Tabs>
  );
}

function CalendarioClientesBody({ readOnly, subTab, onSubTabChange, onOpenParamsWithPlatform }) {
  const primaryTab = CLIENTES_PRIMARY_SUBS.has(subTab) ? subTab : "generales";
  const showLegacy = String(subTab || "").startsWith("legacy-");

  return (
    <Tabs value={showLegacy ? subTab : primaryTab} onValueChange={onSubTabChange} className="space-y-4">
      <TabsList className="rounded-sm flex flex-wrap h-auto gap-1">
        <TabsTrigger value="generales" className="rounded-sm text-xs font-semibold" data-testid="calendario-clientes-generales-tab">
          Mensajes generales
        </TabsTrigger>
        <TabsTrigger value="plantillas" className="rounded-sm text-xs" data-testid="calendario-clientes-plantillas-tab">
          Plantillas
        </TabsTrigger>
        <TabsTrigger value="parametros" className="rounded-sm text-xs" data-testid="calendario-clientes-email-params-tab">
          Parámetros de correo
        </TabsTrigger>
        <TabsTrigger value="envio-libre" className="rounded-sm text-xs" data-testid="calendario-clientes-envio-libre-tab">
          Envío libre
        </TabsTrigger>
        <TabsTrigger value="seguimiento" className="rounded-sm text-xs" data-testid="calendario-clientes-seguimiento-tab">
          Avisos Enviados
        </TabsTrigger>
      </TabsList>

      <TabsContent value="generales" className="mt-0">
        <CalendarioMensajesGeneralesPanel
          readOnly={readOnly}
          onOpenParams={() => onSubTabChange("parametros")}
          onOpenPlantillas={() => onSubTabChange("plantillas")}
        />
      </TabsContent>
      <TabsContent value="plantillas" className="mt-0">
        <CalendarioMensajesPlantillasPanel readOnly={readOnly} />
      </TabsContent>
      <TabsContent value="parametros" className="mt-0">
        <CalendarioEmailParamsPanel
          readOnly={readOnly}
          onOpenEnvioLibre={() => onSubTabChange("envio-libre")}
        />
      </TabsContent>
      <TabsContent value="envio-libre" className="mt-0">
        <CalendarioEnvioLibrePanel
          readOnly={readOnly}
          onOpenParams={onOpenParamsWithPlatform || (() => onSubTabChange("parametros"))}
        />
      </TabsContent>
      <TabsContent value="seguimiento" className="mt-0">
        <DespachoInfoCallout>
          Aquí no están las campañas de correo. Son vencimientos donde ya se marcó que se avisó al cliente
          (desde Tareas / obligaciones). Las campañas se arman en <strong>Mensajes generales</strong>.
        </DespachoInfoCallout>
        <FollowupSentPanel readOnly={readOnly} />
      </TabsContent>

      {/* Legacy oculto: tipos / bandeja por URL */}
      <TabsContent value="legacy-tipos" className="mt-0">
        <DespachoInfoCallout>
          Catálogo de tipos (flujo anterior). Preferimos <strong>Plantillas</strong> de mensajes generales.
          <button type="button" className="ml-2 underline text-[#0F2D5C]" onClick={() => onSubTabChange("plantillas")}>
            Ir a Plantillas
          </button>
        </DespachoInfoCallout>
        <CalendarioClientTemplatesPanel testId="calendario-client-message-types" section="types" />
      </TabsContent>
      <TabsContent value="legacy-bandeja" className="mt-0">
        <DespachoInfoCallout>
          Bandeja del flujo anterior. El estado y reenvío viven en <strong>Mensajes generales</strong>.
          <button type="button" className="ml-2 underline text-[#0F2D5C]" onClick={() => onSubTabChange("generales")}>
            Ir a Mensajes generales
          </button>
        </DespachoInfoCallout>
        <ClientReminderTasksPanel readOnly={readOnly} />
      </TabsContent>
    </Tabs>
  );
}

function CalendarioClientesWhatsappBody() {
  return (
    <div className="space-y-4" data-testid="calendario-clientes-whatsapp-body">
      <DespachoInfoCallout>
        Esta sección tendrá la misma lógica que el correo (mensajes generales por campaña + empresas),
        pero por <strong>WhatsApp Business</strong>. Aún no está activa.
      </DespachoInfoCallout>
      <CalendarioClientWhatsappPanel />
    </div>
  );
}

function CalendarioAutoBody({ readOnly, subTab, onSubTabChange }) {
  return (
    <Tabs value={subTab} onValueChange={onSubTabChange} className="space-y-4">
      <TabsList className="rounded-sm flex flex-wrap h-auto gap-1">
        <TabsTrigger value="interruptores" className="rounded-sm text-xs" data-testid="calendario-auto-interruptores-tab">
          Interruptores automáticos
        </TabsTrigger>
        <TabsTrigger value="tablero" className="rounded-sm text-xs" data-testid="calendario-auto-tablero-tab">
          Recordatorios programados
        </TabsTrigger>
      </TabsList>
      <TabsContent value="interruptores" className="mt-0">
        <CalendarioAutoAlertsPanel />
      </TabsContent>
      <TabsContent value="tablero" className="mt-0">
        <ScheduledAuxRemindersPanel readOnly={readOnly} nested />
      </TabsContent>
    </Tabs>
  );
}

export default function CalendarioDespacho() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  // URL = fuente única. Sin ?tab= (o tab=none) → todas cerradas al entrar / al salir y volver.
  const openSection = useMemo(() => {
    const raw = String(searchParams.get("tab") || "").toLowerCase();
    if (!raw) return SECTION_NONE;
    return SECTION_FROM_QUERY[raw] || SECTION_NONE;
  }, [searchParams]);

  const clientesSub = useMemo(() => {
    const raw = String(searchParams.get("sub") || "").toLowerCase();
    return CLIENTES_SUB_FROM_QUERY[raw] || "generales";
  }, [searchParams]);

  const autoSub = useMemo(() => {
    const raw = String(searchParams.get("sub") || "").toLowerCase();
    return AUTO_SUB_FROM_QUERY[raw] || "interruptores";
  }, [searchParams]);

  const writeQuery = (sectionId, subId, extra = {}) => {
    if (sectionId === SECTION_NONE) {
      setSearchParams({}, { replace: true });
      return;
    }
    const next = new URLSearchParams();
    if (sectionId) next.set("tab", sectionId);
    if (sectionId === SECTION_CLIENTES) {
      if (subId && subId !== "generales") next.set("sub", subId);
    } else if (sectionId === SECTION_AUTO) {
      if (subId && subId !== "interruptores") next.set("sub", subId);
    }
    if (extra.platformSmtp) next.set("platform_smtp", "1");
    setSearchParams(next, { replace: true });
  };

  const openEmailParamsForPlatform = () => {
    writeQuery(SECTION_CLIENTES, "parametros", { platformSmtp: true });
  };

  const toggleSection = (sectionId) => {
    if (openSection === sectionId) {
      writeQuery(SECTION_NONE, null);
      return;
    }
    if (sectionId === SECTION_CLIENTES) writeQuery(sectionId, clientesSub);
    else if (sectionId === SECTION_AUTO) writeQuery(sectionId, autoSub);
    else writeQuery(sectionId, null);
  };

  return (
    <DespachoPageShell
      title="Calendario y Recordatorios"
      subtitle="Reglas fiscales y recordatorios en un solo lugar. Abra o cierre cada sección con la flecha. Generar vencimientos: Tareas → Generar."
      testId="despacho-calendario"
    >
      <div className="space-y-3" data-testid="calendario-acordeon">
        {SECTIONS.map((sec) => {
          const isOpen = openSection === sec.id;
          return (
            <AccordionSection
              key={sec.id}
              title={sec.title}
              description={sec.description}
              isOpen={isOpen}
              onToggle={() => toggleSection(sec.id)}
              testId={sec.testId}
            >
              {sec.id === SECTION_FISCAL ? <CalendarioFiscalBody /> : null}
              {sec.id === SECTION_OPERATIVO ? <CalendarioOperationalRemindersPanel /> : null}
              {sec.id === SECTION_CLIENTES ? (
                <CalendarioClientesBody
                  readOnly={readOnly}
                  subTab={clientesSub}
                  onSubTabChange={(sub) => writeQuery(SECTION_CLIENTES, sub)}
                  onOpenParamsWithPlatform={openEmailParamsForPlatform}
                />
              ) : null}
              {sec.id === SECTION_CLIENTES_WA ? <CalendarioClientesWhatsappBody /> : null}
              {sec.id === SECTION_AUTO ? (
                <CalendarioAutoBody
                  readOnly={readOnly}
                  subTab={autoSub}
                  onSubTabChange={(sub) => writeQuery(SECTION_AUTO, sub)}
                />
              ) : null}
            </AccordionSection>
          );
        })}
      </div>
    </DespachoPageShell>
  );
}
